import { PropertyValueSet } from "@genesys/property";
import { exhaustiveCheck } from "ts-exhaustive-check";
import * as EmissionFromCombustion from "./moisture-load-rules/load-definititions/emission-from-combustion";
import * as EmissionFromPeople from "./moisture-load-rules/load-definititions/emission-from-people";
import * as EvaporationFromMaterials from "./moisture-load-rules/load-definititions/evaporation-from-materials";
import * as EvaporationOfWater from "./moisture-load-rules/load-definititions/evaporation-of-water";
import * as HeatTransmission from "./moisture-load-rules/load-definititions/heat-transmission";
import * as IntentionalVentilation from "./moisture-load-rules/load-definititions/intentional-ventilation";
import * as StaticMoistureLoad from "./moisture-load-rules/load-definititions/static-moisture-load";
import * as StaticHeatLoad from "./moisture-load-rules/load-definititions/static-heat-load";
import * as UnintendedVentilation from "./moisture-load-rules/load-definititions/unintentional-ventilation";
import * as OpenDoor from "./moisture-load-rules/load-definititions/open-door";
import * as Opening from "./moisture-load-rules/load-definititions/opening";
import * as RoomControlSettings from "./moisture-load-rules/room-control-settings";
import * as SystemSettings from "./moisture-load-rules/system-settings";
import * as Building from "./moisture-load-rules/building-settings";
import * as InfiltrationModelSettings from "./moisture-load-rules/infiltration-model-settings";
import { MoistureHeatLoadTypeEnum } from "../../../../graphql-types";
import * as KnownProperties from "./known-properties";
import { PropertyDefinitions } from "./types";
import {
  setPropertyIfValueAllowed,
  definitionIsValid
} from "./helper-functions";

export interface InternalGetPropertyUpdatesFunction {
  (
    previousSelections: PropertyValueSet.PropertyValueSet,
    currentSelections: PropertyValueSet.PropertyValueSet
  ): PropertyValueSet.PropertyValueSet;
}

export interface InternalGetApplicationTypePropertyUpdatesFunction {
  (applicationType: number): ReadonlyArray<{
    readonly propertyName: string;
    readonly propertyValue: number;
  }>;
}

export const propertyUpdates: ReadonlyArray<InternalGetPropertyUpdatesFunction> =
  Array.from([
    "Building",
    "SystemSettings",
    "InfiltrationModelSettings",
    "RoomControlSettings"
  ])
    .concat(Object.keys(MoistureHeatLoadTypeEnum))
    .map(x => getPropertyUpdateFunc(x as Item));

export const applicationTypeUpdates: ReadonlyArray<InternalGetApplicationTypePropertyUpdatesFunction> =
  Array.from([
    "Building",
    "SystemSettings",
    "InfiltrationModelSettings",
    "RoomControlSettings"
  ])
    .concat(Object.keys(MoistureHeatLoadTypeEnum))
    .map(x => getApplicationTypePropertyUpdateFunc(x as Item));

export function getAllPropertyUpdates(
  previousSelections: PropertyValueSet.PropertyValueSet,
  currentSelections: PropertyValueSet.PropertyValueSet,
  propertyDefinitions: PropertyDefinitions,
  doApplicationTypeLogic: boolean
): PropertyValueSet.PropertyValueSet {
  let totalDefaults = doApplicationTypeLogic
    ? getTopLevelPropertyUpdates(
        previousSelections,
        currentSelections,
        propertyDefinitions
      )
    : PropertyValueSet.Empty;

  //   Apply the rule based changes.
  totalDefaults = propertyUpdates.reduce((soFar, current) => {
    const result = current(
      previousSelections,
      PropertyValueSet.merge(soFar, currentSelections)
    );
    return PropertyValueSet.merge(result, soFar);
  }, totalDefaults);

  return totalDefaults;
}

export function getTopLevelPropertyUpdates(
  previousSelections: PropertyValueSet.PropertyValueSet,
  currentSelections: PropertyValueSet.PropertyValueSet,
  propertyDefinitions: PropertyDefinitions
): PropertyValueSet.PropertyValueSet {
  // Check if applicationType changed.
  const applicationTypeChanged =
    PropertyValueSet.getInteger(
      KnownProperties.applicationType,
      currentSelections
    ) !==
    PropertyValueSet.getInteger(
      KnownProperties.applicationType,
      previousSelections
    );

  let defaults = PropertyValueSet.Empty;

  // Ignore if applicationType didn't change or if.
  if (applicationTypeChanged) {
    const selectedApplicationType = PropertyValueSet.getInteger(
      KnownProperties.applicationType,
      currentSelections
    )!;

    //   Get the rule based changes to apply.
    const changesToMake: ReadonlyArray<{
      readonly propertyName: string;
      readonly propertyValue: number;
    }> = applicationTypeUpdates.flatMap(applicationTypeUpdatesPart => {
      return applicationTypeUpdatesPart(selectedApplicationType);
    });

    defaults = changesToMake.reduce((soFar, current) => {
      return setPropertyIfValueAllowed(
        soFar,
        currentSelections,
        current.propertyName,
        current.propertyValue,
        propertyDefinitions
      );
    }, defaults);
  }

  const systemDef = propertyDefinitions.find(
    x => x.name === KnownProperties.systemConfiguration
  )!;

  if (!definitionIsValid(systemDef, currentSelections)) {
    defaults = setPropertyIfValueAllowed(
      defaults,
      currentSelections,
      KnownProperties.systemConfiguration,
      0,
      propertyDefinitions
    );
  }

  return defaults;
}

type Item =
  | keyof typeof MoistureHeatLoadTypeEnum
  | "Building"
  | "SystemSettings"
  | "RoomControlSettings"
  | "InfiltrationModelSettings";

// gives compile time error if not all cases are handled
function getPropertyUpdateFunc(item: Item) {
  switch (item) {
    case "Building":
      return Building.getPropertyUpdates;
    case "SystemSettings":
      return SystemSettings.getPropertyUpdates;
    case "InfiltrationModelSettings":
      return InfiltrationModelSettings.getPropertyUpdates;
    case "HEAT_TRANSMISSION":
      return HeatTransmission.getPropertyUpdates;
    case "EMISSION_FROM_COMBUSTION":
      return EmissionFromCombustion.getPropertyUpdates;
    case "EMISSION_FROM_PEOPLE":
      return EmissionFromPeople.getPropertyUpdates;
    case "EVAPORATION_FROM_MATERIALS":
      return EvaporationFromMaterials.getPropertyUpdates;
    case "EVAPORATION_OF_WATER":
      return EvaporationOfWater.getPropertyUpdates;
    case "INTENTIONAL_VENTILATION":
      return IntentionalVentilation.getPropertyUpdates;
    case "STATIC_MOISTURE_LOAD":
      return StaticMoistureLoad.getPropertyUpdates;
    case "STATIC_HEAT_LOAD":
      return StaticHeatLoad.getPropertyUpdates;
    case "UNINTENDED_VENTILATION":
      return UnintendedVentilation.getPropertyUpdates;
    case "OPEN_DOOR":
      return OpenDoor.getPropertyUpdates;
    case "OPENING":
      return Opening.getPropertyUpdates;
    case "RoomControlSettings":
      return RoomControlSettings.getPropertyUpdates;

    // Remove?
    case "DIFFUSION_THROUGH_MATERIALS":
      return () => PropertyValueSet.Empty;
    default:
      return exhaustiveCheck(item, true);
  }
}

// gives compile time error if not all cases are handled
function getApplicationTypePropertyUpdateFunc(item: Item) {
  switch (item) {
    case "Building":
      return Building.getApplicationTypeUpdates;
    case "SystemSettings":
      return SystemSettings.getApplicationTypeUpdates;
    case "InfiltrationModelSettings":
      return InfiltrationModelSettings.getApplicationTypeUpdates;
    case "HEAT_TRANSMISSION":
      return HeatTransmission.getApplicationTypeUpdates;
    case "EMISSION_FROM_COMBUSTION":
      return EmissionFromCombustion.getApplicationTypeUpdates;
    case "EMISSION_FROM_PEOPLE":
      return EmissionFromPeople.getApplicationTypeUpdates;
    case "EVAPORATION_FROM_MATERIALS":
      return EvaporationFromMaterials.getApplicationTypeUpdates;
    case "EVAPORATION_OF_WATER":
      return EvaporationOfWater.getApplicationTypeUpdates;
    case "INTENTIONAL_VENTILATION":
      return IntentionalVentilation.getApplicationTypeUpdates;
    case "STATIC_MOISTURE_LOAD":
      return StaticMoistureLoad.getApplicationTypeUpdates;
    case "STATIC_HEAT_LOAD":
      return StaticHeatLoad.getApplicationTypeUpdates;
    case "UNINTENDED_VENTILATION":
      return UnintendedVentilation.getApplicationTypeUpdates;
    case "OPEN_DOOR":
      return OpenDoor.getApplicationTypeUpdates;
    case "OPENING":
      return Opening.getApplicationTypeUpdates;
    case "RoomControlSettings":
      return RoomControlSettings.getApplicationTypeUpdates;

    // Remove?
    case "DIFFUSION_THROUGH_MATERIALS":
      return () => [];
    default:
      return exhaustiveCheck(item, true);
  }
}
